import React, { useEffect } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { canAccessRoute } from "@/lib/roleAccess";

/**
 * Route-level access wrapper. If the current user's role isn't allowed to
 * see `route`, they get bounced back to /console/dashboard with a toast
 * explaining why. Renders `children` when access is allowed.
 *
 * Usage:
 *   <Route path="billing" element={<RoleGuard route="/console/billing"><Billing/></RoleGuard>} />
 */
export default function RoleGuard({ route, children }) {
  const { user } = useAuth();
  const location = useLocation();
  const allowed = user && canAccessRoute(route, user.role);

  useEffect(() => {
    if (!allowed && user) {
      toast.error("You don't have access to that page", {
        description: `Your role (${user.role.replace("_", " ")}) can't open ${route}.`,
      });
    }
  }, [allowed, user, route]);

  if (!user) {
    // Not logged in yet — let the outer <ProtectedRoute> handle redirect
    return null;
  }
  if (!allowed) {
    return <Navigate to="/console/dashboard" replace state={{ from: location }} />;
  }
  return children;
}
